import React, { useMemo, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { format } from "date-fns"
import { AlertTriangle, Download, Info, Package } from "lucide-react"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { EmptyState } from "@/components/shared/EmptyState"
import { LoadingState } from "@/components/shared/LoadingState"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  useCollectionsConfig,
  useLocations,
  useRoutesPlanned,
  useVehicleTypes,
} from "@/hooks/useCollections"
import { supabase } from "@/lib/supabase"
import { cn } from "@/lib/utils"
import { RouteInstance } from "@/types/collections"
import { buildCsv, downloadCsv } from "@/lib/csvExport"
import {
  buildWeekdayData,
  comparisonDatesFor,
  inefficientDaysIn,
  lowLoadFactorRoutesIn,
  overallMetricsFor,
  PLANNED_ROUTES_CSV_HEADER,
  plannedRoutesCsvRows,
  type ViewMode,
  WEEKDAY_NAMES,
} from "@/lib/weekdayComparison"

import DailyBreakdownRow from "./weekday/DailyBreakdownRow"
import LoadFactorFilters from "./weekday/LoadFactorFilters"
import OverallMetricsCards from "./weekday/OverallMetricsCards"
import { getLoadFactorBgColor } from "./weekday/loadFactorColors"
import { useDateRangePreset } from "./weekday/useDateRangePreset"

const WeekdayComparison: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>("week")
  const [selectedWeekday, setSelectedWeekday] = useState<number>(1) // Monday = 1
  const { dateRange, setDateRange, datePreset, setDatePreset } = useDateRangePreset()

  const { comparisonDates, datesToFetch } = useMemo(() => {
    const dates = comparisonDatesFor(dateRange, viewMode, selectedWeekday)
    return {
      comparisonDates: dates,
      datesToFetch: [...new Set(dates.map((d) => format(d, "yyyy-MM-dd")))],
    }
  }, [dateRange, selectedWeekday, viewMode])

  const { data: configData } = useCollectionsConfig()

  const routeInstanceQueries = useQuery({
    queryKey: ["collections", "route_instances", "weekday-comparison", datesToFetch],
    queryFn: async () => {
      const allInstances: RouteInstance[] = []

      for (const dateStr of datesToFetch) {
        try {
          const { data, error } = await supabase.functions.invoke("collections", {
            body: {
              action: "get_route_instances_for_date",
              date: dateStr,
            },
          })

          if (!error && data?.success && data?.data) {
            allInstances.push(...(data.data as RouteInstance[]))
          }
        } catch (err) {
          console.error(`Error fetching data for ${dateStr}:`, err)
        }
      }

      return allInstances
    },
    enabled: datesToFetch.length > 0,
    staleTime: 60000,
  })

  const isLoading = routeInstanceQueries.isLoading

  const { data: locationsData } = useLocations()
  const { data: vehicleTypesData } = useVehicleTypes()

  const weekdayData = useMemo(
    () =>
      buildWeekdayData({
        routeInstances: routeInstanceQueries.data || [],
        comparisonDates,
        locations: locationsData?.data,
        vehicleTypes: vehicleTypesData?.data,
        config: configData?.data,
        viewMode,
      }),
    [
      routeInstanceQueries.data,
      comparisonDates,
      locationsData,
      vehicleTypesData,
      viewMode,
      configData,
    ],
  )

  const overallMetrics = useMemo(() => overallMetricsFor(weekdayData), [weekdayData])
  const inefficientDays = useMemo(() => inefficientDaysIn(weekdayData), [weekdayData])
  const lowLoadFactorRoutes = useMemo(() => lowLoadFactorRoutesIn(weekdayData), [weekdayData])

  // Export is by planned route and day, not instances.
  const { data: routesPlannedData } = useRoutesPlanned(undefined, true)

  // Temp export. Mileage = stop 1→2→3… (Haversine).
  const handleExportRoutesCsv = () => {
    const rows = plannedRoutesCsvRows(routesPlannedData?.data ?? [], configData?.data)
    const csvContent = buildCsv(PLANNED_ROUTES_CSV_HEADER, rows)
    downloadCsv(
      `collection-planned-routes-export-${format(new Date(), "yyyy-MM-dd")}.csv`,
      csvContent,
    )
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-auto bg-palette-jungleGreen000 p-6">
      <div className="max-w-7xl mx-auto w-full space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-palette-jungleGreen900">
              Collection Load Factor Analysis
            </h1>
            <p className="text-sm text-palette-jungleGreen500 mt-1">
              Identify inefficient days and low load factor routes
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportRoutesCsv}
              disabled={!routesPlannedData?.data?.length}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Export planned routes CSV
            </Button>
            <p className="text-xs text-palette-jungleGreen500 max-w-[260px] text-right">
              Planned routes only. Mileage includes full route (depot → collections → depot), stop
              1→2→3….
            </p>
          </div>
        </div>

        <LoadFactorFilters
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          selectedWeekday={selectedWeekday}
          onSelectedWeekdayChange={setSelectedWeekday}
          datePreset={datePreset}
          onDatePresetChange={setDatePreset}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />

        <Alert className="border-blue-200 bg-blue-50">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-900">
            <div className="font-medium mb-1">Data Availability</div>
            <div className="text-sm">
              This tool has been in full use since <strong>Monday, January 19, 2026</strong>. Data
              before this date may be incomplete or unavailable.
            </div>
          </AlertDescription>
        </Alert>

        {overallMetrics && (
          <OverallMetricsCards
            metrics={overallMetrics}
            dayCount={weekdayData.length}
            viewMode={viewMode}
          />
        )}

        {inefficientDays.length > 0 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="font-semibold mb-2">Structurally Inefficient Days Detected</div>
              <div className="flex flex-wrap gap-2">
                {inefficientDays.map((day) => (
                  <Badge
                    key={day.date}
                    variant="outline"
                    className={cn("border-red-200", getLoadFactorBgColor(day.loadFactor))}
                  >
                    {day.dateLabel}: {day.loadFactor.toFixed(1)}% ({day.routesIncluded} routes)
                  </Badge>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {isLoading ? (
          <Card>
            <LoadingState label="Loading data..." className="py-12" />
          </Card>
        ) : weekdayData.length === 0 ? (
          <Card>
            <EmptyState>No data available for the selected period</EmptyState>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Load Factor Trend</CardTitle>
                <CardDescription>
                  {viewMode === "weekday"
                    ? `Load factor percentage over time for ${WEEKDAY_NAMES[selectedWeekday]}s`
                    : "Load factor percentage across all days"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={weekdayData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dateLabel" tick={{ fontSize: 12 }} />
                    <YAxis
                      label={{ value: "Load Factor (%)", angle: -90, position: "insideLeft" }}
                      tick={{ fontSize: 12 }}
                      domain={[0, 100]}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => {
                        const label = name === "loadFactorForecast" ? "Forecast" : "Actual"
                        return [`${value.toFixed(1)}%`, label]
                      }}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Legend
                      formatter={(value) =>
                        value === "loadFactorForecast" ? "Forecast" : "Actual"
                      }
                    />
                    <Line
                      type="monotone"
                      dataKey="loadFactorForecast"
                      stroke="#94a3b8"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ r: 4 }}
                      name="loadFactorForecast"
                    />
                    <Line
                      type="monotone"
                      dataKey="loadFactorActual"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      name="loadFactorActual"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Daily Breakdown</CardTitle>
                <CardDescription>Load factor by date with route-level details</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-4 font-medium text-palette-jungleGreen700">
                          Date
                        </th>
                        <th className="text-right py-2 px-4 font-medium text-palette-jungleGreen700">
                          Load Factor (Forecast)
                        </th>
                        <th className="text-right py-2 px-4 font-medium text-palette-jungleGreen700">
                          Load Factor (Actual)
                        </th>
                        <th className="text-right py-2 px-4 font-medium text-palette-jungleGreen700">
                          Routes
                        </th>
                        <th className="text-right py-2 px-4 font-medium text-palette-jungleGreen700">
                          Used
                        </th>
                        <th className="text-right py-2 px-4 font-medium text-palette-jungleGreen700">
                          Capacity
                        </th>
                        <th className="text-center py-2 px-4 font-medium text-palette-jungleGreen700">
                          Details
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {weekdayData.map((point) => {
                        const usedDisplay =
                          point.totalUsedActual > 0
                            ? point.totalUsedActual
                            : point.totalUsedForecast
                        return (
                          <DailyBreakdownRow
                            key={point.date}
                            point={point}
                            usedDisplay={usedDisplay}
                          />
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {lowLoadFactorRoutes.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-blue-600" />
                    Low Load Factor Routes
                  </CardTitle>
                  <CardDescription>
                    Days with multiple low-load routes that could be consolidated
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {lowLoadFactorRoutes.map((opp) => (
                      <div
                        key={opp.date}
                        className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200"
                      >
                        <div className="flex-1">
                          <div className="font-semibold text-palette-jungleGreen900">
                            {opp.dateLabel}
                          </div>
                          <div className="text-sm text-palette-jungleGreen600 mt-1">
                            {opp.lowLoadRoutes} routes at {opp.avgLoadFactor.toFixed(1)}% avg load
                          </div>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {opp.routeCodes.map((code, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {code}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-lg font-bold text-amber-600">
                            {opp.totalWastedCapacity.toFixed(1)}
                          </div>
                          <div className="text-xs text-palette-jungleGreen500">
                            UK pallets wasted
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default WeekdayComparison
